import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../services/supabaseClient';
import Combobox from '../../components/common/Combobox';
import PlacesAutocomplete from '../../components/common/PlacesAutocomplete';
import professionsData from '../../data/professionsData';
import instrumentsData from '../../data/instrumentsData';
import { isValidNumber, isValidFutureDate } from '../../utils/validation';
import './CreateGig.css';

/**
 * CreateGig component for venues to post new gig opportunities
 */
const CreateGig = () => {
  const navigate = useNavigate();
  const [, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  // Field validation state
  const [validationErrors, setValidationErrors] = useState({
    title: '',
    date: '',
    time: '',
    location: '',
    pay: '',
    position: ''
  });
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    duration: '',
    location: '',
    location_data: null,
    pay: '',
    payment_type: 'flat',
    position: '',
    instruments: [],
    experience_level: 'any',
    is_recurring: false
  });
  
  const [showMusicianOptions, setShowMusicianOptions] = useState(false);

  useEffect(() => {
    const checkUserProfile = async () => {
      try {
        setLoading(true);
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user) {
          setUser(user);
          
          // Fetch the user's profile
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('*')
            .eq('uuid', user.id)
            .single();
          
          if (profileError) throw profileError;
          
          setUserProfile(profile);
          
          // Redirect if not a venue
          if (profile.user_type !== 'venue') {
            navigate('/dashboard');
            return;
          }
        } else {
          navigate('/login');
          return;
        }
      } catch (err) {
        console.error('Error checking user profile:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    checkUserProfile();
  }, [navigate]);

  // Validation functions
  const validateTitle = (title) => {
    if (!title || title.trim() === '') {
      return 'Gig title is required';
    }
    if (title.length < 5) {
      return 'Title must be at least 5 characters long';
    }
    return '';
  };

  const validateDate = (date) => {
    if (!date) {
      return 'Date is required';
    }
    if (!isValidFutureDate(date)) {
      return 'Date must be in the future';
    }
    return '';
  };

  const validateTime = (time) => {
    if (!time) {
      return 'Time is required';
    }
    return '';
  };

  const validateLocation = (location) => {
    if (!location) {
      return 'Location is required';
    }
    return '';
  };

  const validatePay = (pay) => {
    if (!pay) {
      return 'Payment amount is required';
    }
    if (!isValidNumber(pay, 0)) {
      return 'Payment must be a valid number greater than or equal to 0';
    }
    return '';
  };

  const validatePosition = (position) => {
    if (!position) {
      return 'Position type is required';
    }
    return '';
  };

  // Validate instruments if position is Musician
  const validateInstruments = (position, instruments) => {
    if (position === 'Musician' && (!instruments || instruments.length === 0)) {
      return 'Please select at least one instrument';
    }
    return '';
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
    
    // Validate the field as it changes
    let validationError = '';
    switch (name) {
      case 'title':
        validationError = validateTitle(newValue);
        break;
      case 'date':
        validationError = validateDate(newValue);
        break;
      case 'time':
        validationError = validateTime(newValue);
        break;
      case 'location':
        validationError = validateLocation(newValue);
        break;
      case 'pay':
        validationError = validatePay(newValue);
        break;
      case 'position':
        validationError = validatePosition(newValue);
        // Also update instruments validation if position changes
        const instrumentsError = validateInstruments(newValue, formData.instruments);
        setValidationErrors(prev => ({ ...prev, instruments: instrumentsError }));
        break;
      default:
        break;
    }
    
    setValidationErrors(prev => ({
      ...prev,
      [name]: validationError
    }));
  };

  // Handle location selection from PlacesAutocomplete
  const handleLocationSelect = (address, locationData) => {
    setFormData(prev => ({
      ...prev,
      location: address,
      location_data: locationData
    }));
    
    setValidationErrors(prev => ({
      ...prev,
      location: validateLocation(address)
    }));
  };

  // Handle instruments selection
  const handleInstrumentsChange = (selectedInstruments) => {
    setFormData(prev => ({
      ...prev,
      instruments: selectedInstruments
    }));
    
    setValidationErrors(prev => ({
      ...prev,
      instruments: validateInstruments(formData.position, selectedInstruments)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields before submission
    const titleError = validateTitle(formData.title);
    const dateError = validateDate(formData.date);
    const timeError = validateTime(formData.time);
    const locationError = validateLocation(formData.location);
    const payError = validatePay(formData.pay);
    const positionError = validatePosition(formData.position);
    const instrumentsError = validateInstruments(formData.position, formData.instruments);
    
    const newValidationErrors = {
      title: titleError,
      date: dateError,
      time: timeError,
      location: locationError,
      pay: payError,
      position: positionError,
      instruments: instrumentsError
    };
    
    setValidationErrors(newValidationErrors);
    
    // Check if there are any validation errors
    const hasErrors = Object.values(newValidationErrors).some(error => error !== '');
    
    if (hasErrors) {
      setError('Please fix the validation errors before submitting');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      if (!userProfile) {
        throw new Error('User profile not found');
      }
      
      if (userProfile.user_type !== 'venue') {
        throw new Error('Only venues can create gigs');
      }
      
      // Format the instruments data based on position
      let instrumentsValue = '';
      if (formData.position === 'Musician' && formData.instruments.length > 0) {
        instrumentsValue = formData.instruments.join(', ');
      } else if (formData.position) {
        instrumentsValue = formData.position;
      }
      
      // Format the data for submission
      const gigData = {
        title: formData.title,
        description: formData.description,
        date: formData.date,
        time: formData.time,
        duration: formData.duration,
        location: formData.location, // Store the full formatted address
        // Store location coordinates for map display
        location_lat: formData.location_data?.lat,
        location_lng: formData.location_data?.lng,
        pay: parseFloat(formData.pay) || 0,
        payment_type: formData.payment_type,
        instruments: instrumentsValue,
        experience_level: formData.experience_level,
        is_recurring: formData.is_recurring,
        created_by: userProfile.uuid,
        venue_id: userProfile.uuid,
        venue_name: userProfile.venue_name || userProfile.full_name,
        status: 'open'
      };
      
      console.log('Using venue_id (UUID) from profile:', userProfile?.uuid);
      
      console.log('Gig data being sent to Supabase:', gigData);
      
      // Create gig in database
      const { data, error } = await supabase
        .from('gigs')
        .insert([gigData])
        .select();
      
      console.log('Supabase response:', { data, error });
        
      if (error) {
        console.error('Supabase error details:', error);
        throw error;
      }
      
      setSuccess(true);
      console.log('Gig created successfully:', data);
      
      // Redirect to the newly created gig after a short delay
      setTimeout(() => {
        if (data && data[0] && data[0].id) {
          navigate(`/gigs/${data[0].id}`);
        } else {
          console.error('No gig ID returned from Supabase');
          navigate('/gigs');
        }
      }, 2000);
      
    } catch (error) {
      console.error('Error creating gig:', error.message);
      console.error('Error details:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (error && error.includes('Only venues and organizations can post gigs')) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger">{error}</div>
        <button 
          className="btn btn-primary" 
          onClick={() => navigate('/profile')}
        >
          Update Profile
        </button>
      </div>
    );
  }

  return (
    <div className="create-gig-container">
      <div className="dashboard-header">
        <h1>Post a New Gig</h1>
        <p>Find the perfect musician for your venue</p>
      </div>
      
      {success && (
        <div className="alert alert-success">
          Gig created successfully! Redirecting to gig details...
        </div>
      )}
      
      {error && <div className="alert alert-danger">{error}</div>}
      
      <form onSubmit={handleSubmit}>
        <div className="card mb-4">
          <div className="card-body">
            <h5 className="card-title">Basic Information</h5>
            
            <div className="mb-3">
              <label htmlFor="title" className="form-label">Gig Title *</label>
              <input
                type="text"
                className={`form-control ${validationErrors.title ? 'is-invalid' : ''}`}
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
              />
              {validationErrors.title && <div className="invalid-feedback">{validationErrors.title}</div>}
            </div>
            
            <div className="mb-3">
              <label htmlFor="description" className="form-label">Description *</label>
              <textarea
                className="form-control"
                id="description"
                name="description"
                rows="3"
                value={formData.description}
                onChange={handleChange}
              ></textarea>
            </div>
            
            <div className="row">
              <div className="col-md-4 mb-3">
                <label htmlFor="date" className="form-label">Date *</label>
                <input
                  type="date"
                  className={`form-control ${validationErrors.date ? 'is-invalid' : ''}`}
                  id="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  required
                />
                {validationErrors.date && <div className="invalid-feedback">{validationErrors.date}</div>}
              </div>
              
              <div className="col-md-4 mb-3">
                <label htmlFor="time" className="form-label">Time *</label>
                <input
                  type="time"
                  className={`form-control ${validationErrors.time ? 'is-invalid' : ''}`}
                  id="time"
                  name="time"
                  value={formData.time}
                  onChange={handleChange}
                  required
                />
                {validationErrors.time && <div className="invalid-feedback">{validationErrors.time}</div>}
              </div>
              
              <div className="col-md-4 mb-3">
                <label htmlFor="duration" className="form-label">Duration</label>
                <input
                  type="text"
                  className="form-control"
                  id="duration"
                  name="duration"
                  placeholder="e.g. 2 hours"
                  value={formData.duration}
                  onChange={handleChange}
                />
              </div>
            </div>
            
            <div className="mb-3">
              <label htmlFor="location" className="form-label">Location *</label>
              <PlacesAutocomplete
                onSelect={handleLocationSelect}
                className={validationErrors.location ? 'is-invalid' : ''}
                value={formData.location}
                onChange={(address, locationDetails) => {
                  console.log('Location selected for gig:', address);
                  console.log('Location details for gig:', locationDetails);
                  setFormData({
                    ...formData,
                    location: address, // This is what gets saved to the 'location' column
                    location_data: locationDetails
                  });
                }}
                placeholder="Search for a location"
                required={true}
              />
              {validationErrors.location && <div className="invalid-feedback">{validationErrors.location}</div>}
              <small className="form-text text-muted">Enter the full address where the gig will take place</small>
            </div>
          </div>
        </div>
        
        <div className="card mb-4">
          <div className="card-body">
            <h5 className="card-title">Payment Information</h5>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label htmlFor="pay" className="form-label">Payment Amount *</label>
                <div className="input-group">
                  <span className="input-group-text">$</span>
                  <input
                    type="number"
                    className={`form-control ${validationErrors.pay ? 'is-invalid' : ''}`}
                    id="pay"
                    name="pay"
                    min="0"
                    step="0.01"
                    value={formData.pay}
                    onChange={handleChange}
                    required
                  />
                </div>
                {validationErrors.pay && <div className="invalid-feedback d-block">{validationErrors.pay}</div>}
              </div>
              <div className="col-md-6 mb-3">
                <label htmlFor="payment_type" className="form-label">Payment Type</label>
                <select
                  className="form-select"
                  id="payment_type"
                  name="payment_type"
                  value={formData.payment_type}
                  onChange={handleChange}
                >
                  <option value="flat">Flat Rate</option>
                  <option value="hourly">Hourly</option>
                  <option value="performance">Per Performance</option>
                  <option value="budget">Budget</option>
                </select>
              </div>
            </div>
          </div>
        </div>
        
        <div className="card mb-4">
          <div className="card-body">
            <h5 className="card-title">Requirements</h5>
            
            <div className="mb-3">
              <label className="form-label">Position Type *</label>
              <select
                name="position"
                id="position"
                className={`form-select ${validationErrors.position ? 'is-invalid' : ''}`}
                value={formData.position}
                onChange={(e) => {
                  const selectedPosition = e.target.value;
                  handleChange(e);

                  // Reset instruments if position changes
                  setFormData((prev) => ({
                    ...prev,
                    position: selectedPosition,
                    instruments: [],
                  }));

                  // Show musician options if position is Musician
                  setShowMusicianOptions(selectedPosition === 'Musician');
                }}
                required
              >
                <option value="">Select position</option>
                {professionsData.map((profession) => (
                  <option key={profession} value={profession}>
                    {profession}
                  </option>
                ))}
              </select>
              {validationErrors.position && <div className="invalid-feedback">{validationErrors.position}</div>}
              <small className="form-text text-muted">Select the type of professional you need</small>
            </div>
            
            {showMusicianOptions && (
              <div className="mb-3">
                <label htmlFor="instruments" className="form-label">Instrument(s) Needed *</label>
                <Combobox
                  id="instruments"
                  options={instrumentsData}
                  selectedValues={formData.instruments || []}
                  onChange={handleInstrumentsChange}
                  placeholder="Select instruments needed"
                  allowCustomValues={true}
                  multiple={true}
                />
                {validationErrors.instruments && <div className="invalid-feedback d-block">{validationErrors.instruments}</div>}
                <small className="form-text text-muted">Select the instruments needed for this gig</small>
              </div>
            )}
            
            <div className="row">
              <div className="col-md-6 mb-3">
                <label htmlFor="experience_level" className="form-label">Experience Level</label>
                <select
                  className="form-select"
                  id="experience_level"
                  name="experience_level"
                  value={formData.experience_level}
                  onChange={handleChange}
                >
                  <option value="any">Any</option>
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                  <option value="professional">Professional</option>
                </select>
              </div>
              
              <div className="col-md-6 mb-3">
                <div className="form-check mt-4">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="is_recurring"
                    name="is_recurring"
                    checked={formData.is_recurring}
                    onChange={handleChange}
                  />
                  <label className="form-check-label" htmlFor="is_recurring">
                    This is a recurring gig
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="d-flex justify-content-between">
          <button 
            type="button" 
            className="btn btn-secondary"
            onClick={() => navigate('/gigs')}
          >
            Cancel
          </button>
          
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Gig'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateGig;
